import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { PlaceMatchSection } from "./PlaceMatchSection";
import { placeMatchApi, placeMatchKeys, type PlaceMatchSearchResult } from "./placeMatchApi";

/**
 * The `/v/:venueId/settings/place-match` screen (`rwg-place-matching`, §C.1) — the RwG readiness gate.
 * Managers confirm the venue's verified Google place: run a candidate search, confirm / none-match /
 * override, and see the current match status + the R5 re-check prompt. Its query keys under
 * `["venue", venueId, "place-match"]` so `venue.location_confirmed` events invalidate it. The server
 * is authoritative: a stale write (409) surfaces via the mutation error and re-reads the status.
 */
export function PlaceMatchPage() {
  const { venueId } = useVenue();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState<PlaceMatchSearchResult | null>(null);

  const status = useQuery({
    queryKey: placeMatchKeys.status(venueId),
    queryFn: () => placeMatchApi.getStatus(venueId),
  });

  const searchMutation = useMutation({
    mutationFn: () => placeMatchApi.search(venueId),
    onSuccess: (result) => setSearch(result),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: placeMatchKeys.status(venueId) });

  const confirmMutation = useMutation({
    mutationFn: (placeId: string) =>
      placeMatchApi.confirm(venueId, placeId, status.data?.version ?? 0),
    onSuccess: invalidate,
  });

  const noneMutation = useMutation({
    mutationFn: () => placeMatchApi.markNone(venueId, status.data?.version ?? 0),
    onSuccess: invalidate,
  });

  return (
    <PageScaffold title={t("placeMatch.pageTitle")}>
      {status.isLoading && <p>{t("placeMatch.loading")}</p>}
      {status.isError && (
        <p role="alert" className={css({ color: "fg.error" })}>
          {t("placeMatch.loadError")}
        </p>
      )}
      {status.data && (
        <PlaceMatchSection
          status={status.data}
          search={search}
          searching={searchMutation.isPending}
          saving={confirmMutation.isPending || noneMutation.isPending}
          onSearch={() => searchMutation.mutate()}
          onConfirm={(placeId) => confirmMutation.mutate(placeId)}
          onNone={() => noneMutation.mutate()}
        />
      )}
    </PageScaffold>
  );
}
